import { Box, Typography } from '@mui/material';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Group } from '@work4all/models/lib/Classes/Group.entity';

import { SelectableTree } from '../../dataDisplay/tree/SelectableTree';
import {
  createTreeData,
  ROOT_ID,
  toNodeId,
} from '../../dataDisplay/tree/utils';
import { useTreeFilter } from '../../hooks/useTreeFilter';

import { FilterTextInput } from './components/FilterTextInput';

export type GroupPickerItem<TId> = Omit<Group, 'id'> & { id?: TId };

interface GroupPickerProps<TId> {
  groups: GroupPickerItem<TId>[] | null;
  value: GroupPickerItem<TId>[] | null;
  onChange: (value: GroupPickerItem<TId>[]) => void;
  multiple?: boolean;
  expanded?: string[];
  onNodeToggle?: (event: React.SyntheticEvent, nodeIds: string[]) => void;
  className?: string;
  /** Show search input field */
  showSearch?: boolean;
}

export function GroupPicker<TId = number>(props: GroupPickerProps<TId>) {
  const {
    value,
    onChange,
    groups,
    multiple = true,
    expanded,
    onNodeToggle,
    className,
    showSearch = true,
  } = props;

  const { t } = useTranslation();
  const selectableTreeRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { treeData, groupById, groupsByParentId } = useMemo(
    () => createTreeData(groups),
    [groups]
  );

  // Use custom hook for filtering functionality
  const { filteredData: filteredTreeData, searchProps } = useTreeFilter(
    treeData,
    groups,
    {
      showSearch,
      filterPredicate: useCallback(
        (group, searchString) =>
          group.name?.toLowerCase().includes(searchString.toLowerCase()) ??
          false,
        []
      ),
      itemToTreeNode: useCallback(
        (group) => ({
          id: toNodeId(group?.id),
          label: (
            <Typography component="div">
              <span>{group.name}</span>
            </Typography>
          ),
          children: undefined, // Flat list, no children
        }),
        []
      ),
    }
  );

  const selected = useMemo<string[]>(() => {
    if (!value) {
      return [];
    }

    // TODO We need to manually add the ids of fully selected group nodes here,
    // so that MUI considers them selected for the purposes of selection state
    // management. Otherwise there will be no way to deselect them.
    //
    // This should be removed after reworking SelectableTree to handle all
    // selection logic by itself.
    //
    // Note: Right now this is not required, because the filter value cannot be
    // set from any other source. And after the SelectableTree component is
    // reworked, this will not be necessary anymore.

    return value.map((item) => toNodeId(item?.id));
  }, [value]);

  // When the selection changes, we need to update the selected groups:
  // 1. Deselect items that are no longer selected. For groups, recursively
  //    deselect all children.
  // 2. Select the newly selected items. For groups, recursively select all
  //    children.
  // 3. If a group is selected, but some of its children are not, remove it from
  //    selection.
  // 4. For every node that is selected, check if its parent should now also be
  //    added to the selection.
  // 5. Remove duplicates.
  //
  // Steps 1, 2 and 5 should work the same for all types of trees. Steps 3 and 4
  // are unique to this Group tree, because we need to manually add group nodes
  // to the selection if all their children leaf nodes are selected.
  //
  // After the requirements are clarified and this version is reviewed, we can
  // move some of this duplicated code to the SelectableTree component and have
  // it manage all group/leaf node interactions.
  //
  // Here we would only have to manually select/deselect group nodes when its
  // child leaf nodes are selected/deselected.
  //
  // TODO Refactor selection state management for SelectableTree.

  const handleChange = (newSelected: string[]) => {
    function getGroupWithChildren(id: string): GroupPickerItem<TId>[] {
      const group = groupById.get(id);

      if (!group) {
        return [];
      }

      const children = groupsByParentId.get(id) ?? [];

      return [group, ...children.flatMap((id) => getGroupWithChildren(id))];
    }

    if (!multiple) {
      onChange(newSelected.flatMap((id) => getGroupWithChildren(id)));
      return;
    }

    const removed = selected.filter((id) => !newSelected.includes(id));
    const added = newSelected.filter((id) => !selected.includes(id));

    const removedGroups = removed.flatMap((id) => getGroupWithChildren(id));
    const addedGroups = added.flatMap((id) => getGroupWithChildren(id));

    let preDraft = [...addedGroups];

    if (value) {
      preDraft = preDraft.concat(value);
    }

    for (const group of removedGroups) {
      preDraft = preDraft.filter((g) => g.id !== group.id);
    }

    const draft = new Set(preDraft);

    // TODO Add some memoization here to avoid checking the same group multiple
    // times.

    // Remove groups that have children when not all children are selected.

    function isRecursivelySelected(group: GroupPickerItem<TId>) {
      const id = toNodeId(group?.id);

      const children = groupsByParentId.get(id);

      if (!children) {
        return draft.has(group);
      }

      return children.every((id) => isRecursivelySelected(groupById.get(id)));
    }

    for (const group of draft) {
      if (!isRecursivelySelected(group)) {
        draft.delete(group);
      }
    }

    // Add groups where all children are selected.

    function shouldParentBeAdded(group: GroupPickerItem<TId>) {
      const parentId = toNodeId(group?.parentId);

      if (parentId === ROOT_ID) {
        return false;
      }

      const parent = groupById.get(parentId);

      if (!parent) {
        return false;
      }

      return isRecursivelySelected(parent);
    }

    for (const group of draft) {
      if (shouldParentBeAdded(group)) {
        const parent = groupById.get(toNodeId(group.parentId));

        draft.add(parent);
      }
    }

    const asArray = [...draft];

    onChange(asArray);
  };

  //Adding 2px margin to prevent unnecessary scrollbar in picker popovers
  return (
    <Box mb="2px" className={className}>
      {showSearch && (
        <div style={{ paddingLeft: '0.5rem' }}>
          <FilterTextInput
            ref={inputRef}
            autoFocus
            value={searchProps.value}
            onChange={searchProps.onChange}
            placeholder={searchProps.placeholder}
            onKeyDown={searchProps.onKeyDown}
          />
        </div>
      )}
      <div ref={selectableTreeRef}>
        <SelectableTree
          multiple={multiple}
          selectable="all"
          data={filteredTreeData}
          selected={selected}
          onChange={(value) => {
            Array.isArray(value) ? handleChange(value) : handleChange([value]);
          }}
          expanded={expanded}
          onNodeToggle={onNodeToggle}
        />
      </div>
    </Box>
  );
}
